import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface CodeAnimationProps {
  code: string;
  language?: string;
}

const CodeAnimation: React.FC<CodeAnimationProps> = ({ 
  code = `function helloWorld() {\n\n}\n\nhelloWorld();`, 
  language = 'javascript' 
}) => {
  const { theme } = useTheme();
  const [displayedCode, setDisplayedCode] = useState('');
  const [cursorVisible, setCursorVisible] = useState(true);
  const [isTyping, setIsTyping] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLPreElement>(null);

  useEffect(() => {
    let currentIndex = 0;
    let typingInterval: NodeJS.Timeout;

    // Start typing animation
    if (isTyping) {
      typingInterval = setInterval(() => {
        if (currentIndex < code.length) {
          setDisplayedCode(code.substring(0, currentIndex + 1));
          currentIndex++;
        } else {
          setIsTyping(false);
          clearInterval(typingInterval);
        }
      }, 30); // Adjust speed as needed
    }

    // Cursor blinking effect
    const cursorInterval = setInterval(() => {
      setCursorVisible(prev => !prev);
    }, 500);

    return () => {
      clearInterval(typingInterval);
      clearInterval(cursorInterval);
    };
  }, [code, isTyping]);

  // Prevent scrolling when animation is active
  useEffect(() => {
    // Save current scroll position
    const scrollPosition = window.scrollY;
    
    // Ensure container doesn't auto-scroll
    if (containerRef.current && contentRef.current) {
      // Keep scroll position at top of content
      containerRef.current.scrollTop = 0;
    }
    
    // Restore window scroll position
    window.scrollTo(0, scrollPosition);
  }, [displayedCode]);

  const getThemeClass = () => {
    return theme === 'dark' 
      ? 'bg-[#0d1117] text-[#c9d1d9] border-[#30363d]' 
      : 'bg-[#f6f8fa] text-[#24292f] border-[#d0d7de]';
  };

  const getHeaderClass = () => {
    return theme === 'dark'
      ? 'bg-[#161b22] border-[#30363d]'
      : 'bg-[#f6f8fa] border-[#d0d7de]';
  };

  return (
    <div 
      ref={containerRef}
      className={`rounded-lg overflow-hidden shadow-lg max-h-80 no-scrollbar border ${getThemeClass()}`}
      style={{ maxWidth: '100%' }}
    >
      <div className={`flex items-center px-4 py-2 ${getHeaderClass()} border-b`}>
        <div className="flex space-x-2">
          <div className="w-3 h-3 rounded-full bg-[#ff5f56]"></div>
          <div className="w-3 h-3 rounded-full bg-[#ffbd2e]"></div>
          <div className="w-3 h-3 rounded-full bg-[#27c93f]"></div>
        </div>
        <div className="ml-4 text-xs text-gray-400">{language}</div>
      </div>
      <div className="p-4 font-mono text-sm overflow-x-auto">
        <pre ref={contentRef} className="whitespace-pre-wrap">
          {displayedCode}
          <span className={`${cursorVisible ? 'opacity-100' : 'opacity-0'} transition-opacity duration-100 inline-block w-2 h-4 ${theme === 'dark' ? 'bg-[#58a6ff]' : 'bg-[#0969da]'} ml-0.5`}></span>
        </pre>
      </div>
    </div>
  );
};

export default CodeAnimation;